import { call, put, takeLatest } from "redux-saga/effects";

import { message } from "antd";
import { translate } from "utils";
import { PayloadAction } from "@reduxjs/toolkit";
import {
	ApproveCollaboratorPayload,
	BlockCollaboratorPayload,
	GetCollaboratorPayload,
	GetCollaboratorsPayload,
	RejectCollaboratorPayload,
	UpdateCollaboratorPayload,
	approveCollaboratorAsync,
	blockCollaboratorAsync,
	getCollaboratorAsync,
	getCollaboratorsAsync,
	rejectCollaboratorAsync,
	setCollaborator,
	setCollaborators,
	updateCollaboratorAsync,
} from "./collaborator.slice";
import {
	approveCollaboratorAPI,
	blockCollaboratorAPI,
	getCollaboratorAPI,
	getCollaboratorsAPI,
	rejectCollaboratorAPI,
	updateCollaboratorAPI,
} from "service/apis/collaborator";
import { t } from "@lingui/macro";
import { COLLABORATOR_STATUS } from "../type.d";


function* getCollaborators(action: PayloadAction<GetCollaboratorsPayload>) {
	const { query, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await getCollaboratorsAPI(query).then(result => result);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	callback && callback();
	if (response) {
		yield put({ type: setCollaborators.type, payload: response.data });
	} else {
		message.error(translate(error.message));
	}
}

function* watchGetCollaborator() {
	yield takeLatest(getCollaboratorAsync, getCollaborator);
}

function* getCollaborator(action: PayloadAction<GetCollaboratorPayload>) {
	const { id, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await getCollaboratorAPI(id).then(result => result);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	callback && callback();

	if (response) {
		yield put({ type: setCollaborator.type, payload: response.data });
	} else {
		message.error(translate(error.message));
	}
}

function* watchGetCollaborators() {
	yield takeLatest(getCollaboratorsAsync, getCollaborators);
}

function* rejectCollaborator(action: PayloadAction<RejectCollaboratorPayload>) {
	const { id, reason, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await rejectCollaboratorAPI({ id, reason }).then(
				result => result,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	callback && callback();

	if (response) {
		if (response.data.status === COLLABORATOR_STATUS.REJECTED) {
			message.success(
				translate("This collaborator was rejected successfully"),
			);
		} else {
			message.error(translate("Fail to reject this collaborator"));
		}
		yield put({ type: setCollaborator.type, payload: response.data });
	} else {
		message.error(translate(error.message));
	}
}

function* watchRejectCollaborator() {
	yield takeLatest(rejectCollaboratorAsync, rejectCollaborator);
}

function* blockCollaborator(action: PayloadAction<BlockCollaboratorPayload>) {
	const { id, reason, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await blockCollaboratorAPI({ id, reason }).then(
				result => result,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	callback && callback();

	if (response) {
		if (response.data.status === COLLABORATOR_STATUS.BLOCKED) {
			message.success(translate("This collaborator was blocked successfully"));
		} else {
			message.error(translate("Fail to block this collaborator"));
		}
		yield put({ type: setCollaborator.type, payload: response.data });
	} else {
		message.error(translate(error.message));
	}
}

function* watchBlockCollaborator() {
	yield takeLatest(blockCollaboratorAsync, blockCollaborator);
}

function* approveCollaborator(
	action: PayloadAction<ApproveCollaboratorPayload>,
) {
	const { id, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await approveCollaboratorAPI({ id }).then(
				result => result,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	callback && callback();

	if (response) {
		if (response.data.status === COLLABORATOR_STATUS.ENABLE) {
			message.success(
				translate("This collaborator was approved successfully"),
			);
		} else {
			message.error(translate("Fail to approve this collaborator"));
		}

		yield put({ type: setCollaborator.type, payload: response.data });
	} else {
		message.error(translate(error.message));
	}
}

function* watchApproveCollaborator() {
	yield takeLatest(approveCollaboratorAsync, approveCollaborator);
}

function* updateCollaborator(action: PayloadAction<UpdateCollaboratorPayload>) {
	const { data, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await updateCollaboratorAPI(data).then(result => result);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	callback && callback();

	if (response) {
		yield put({ type: setCollaborator.type, payload: response.data });
		message.success(t`Update successful`);
	} else {
		message.error(translate(error.message));
	}
}

function* watchUpdateCollaborator() {
	yield takeLatest(updateCollaboratorAsync, updateCollaborator);
}

const collaboratorSaga = [
	watchGetCollaborators(),
	watchGetCollaborator(),
	watchRejectCollaborator(),
	watchApproveCollaborator(),
	watchBlockCollaborator(),
	watchUpdateCollaborator(),
];

export default collaboratorSaga;
